import { useEffect, useMemo, useState } from 'react';
import {
  financeiroApi,
  type ItemLancamentoEntrada,
  type TipoDescontoItem,
} from '@/features/financeiro/api/financeiroApi';
import {
  orcamentosApi,
  type OrcamentoDetalhe,
} from '@/features/orcamentos/api/orcamentosApi';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { InputMonetario } from '@/shared/ui/InputMonetario';

const fmtMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = new Intl.DateTimeFormat('pt-BR');

function isoDaqui(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

function arred(v: number): number {
  return Math.round(v * 100) / 100;
}

function descontoItem(
  base: number,
  tipo: TipoDescontoItem | null | undefined,
  valor: number | null | undefined,
): number {
  if (!tipo || !valor || valor <= 0) return 0;
  const bruto = tipo === 'Percentual' ? (base * valor) / 100 : valor;
  return arred(Math.min(bruto, base));
}

function liquidoItem(it: ItemLancamentoEntrada): number {
  const bruto = arred(it.quantidade * it.valorUnitario);
  return arred(bruto - descontoItem(bruto, it.descontoTipo, it.descontoValor));
}

type Props = {
  orcamentoId: string;
  aoFechar: () => void;
  aoSalvar?: () => void;
};

export function CriarContaReceberDoOrcamentoModal({ orcamentoId, aoFechar, aoSalvar }: Props) {
  const [orcamento, setOrcamento] = useState<OrcamentoDetalhe | null>(null);
  const [itensOrcamento, setItensOrcamento] = useState<ItemLancamentoEntrada[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [vencimento, setVencimento] = useState(isoDaqui(30));
  const [observacoes, setObservacoes] = useState('');
  const [valor, setValor] = useState(0);
  const [usarItens, setUsarItens] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);
    Promise.all([
      orcamentosApi.obter(orcamentoId),
      financeiroApi.itensDoOrcamento(orcamentoId),
    ])
      .then(([orc, itens]) => {
        if (cancelado) return;
        setOrcamento(orc);
        setItensOrcamento(itens);
        setDescricao(`Recebimento ordem de serviço ${orc.numero}`);
        setValor(orc.total);
        const obs = orc.observacoes ? `Ordem de serviço ${orc.numero} — ${orc.observacoes}` : '';
        setObservacoes(obs);
      })
      .catch((e: unknown) => {
        if (!cancelado) setErro(e instanceof Error ? e.message : 'Falha ao carregar ordem de serviço.');
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [orcamentoId]);

  const totalItens = useMemo(
    () => arred(itensOrcamento.reduce((acc, it) => acc + liquidoItem(it), 0)),
    [itensOrcamento],
  );

  async function aoSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!orcamento) return;
    if (descricao.trim().length < 2) {
      setErro('Descrição inválida.');
      return;
    }
    const valorFinal = usarItens && itensOrcamento.length > 0 ? totalItens : valor;
    if (valorFinal <= 0) {
      setErro('Valor deve ser maior que zero.');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      await financeiroApi.criar({
        tipo: 'Receber',
        descricao: descricao.trim(),
        categoria: categoria.trim() || null,
        valor: valorFinal,
        vencimento: new Date(vencimento).toISOString(),
        clienteId: orcamento.clienteId,
        orcamentoId: orcamento.id,
        observacoes: observacoes.trim() || null,
        itens: usarItens && itensOrcamento.length > 0 ? itensOrcamento : null,
      });
      setSucesso(true);
      aoSalvar?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao criar conta a receber.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo="Criar conta a receber"
      onFechar={aoFechar}
      rodape={
        sucesso ? (
          <Button variante="primary" onClick={aoFechar}>
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={aoFechar} disabled={salvando}>
              Cancelar
            </Button>
            <Button
              variante="primary"
              onClick={(e) => void aoSubmit(e as unknown as React.FormEvent)}
              disabled={salvando || carregando}
            >
              {salvando ? 'Criando…' : 'Criar conta a receber'}
            </Button>
          </>
        )
      }
    >
      {carregando ? (
        <p className="hint">Carregando ordem de serviço…</p>
      ) : sucesso ? (
        <div className="sucesso-bloco" role="status">
          <div className="sucesso-bloco__icone" aria-hidden="true">✓</div>
          <h4 className="sucesso-bloco__titulo">Conta a receber criada</h4>
          <p className="sucesso-bloco__texto">
            O lançamento foi vinculado à ordem de serviço <strong>{orcamento?.numero}</strong> e já
            aparece na tela <strong>Financeiro &rsaquo; Contas a Receber</strong>.
          </p>
        </div>
      ) : !orcamento ? (
        <p className="alert alert--error" role="alert">
          {erro ?? 'Ordem de serviço não encontrada.'}
        </p>
      ) : (
        <form className="form-grid" onSubmit={aoSubmit}>
          {erro && <div className="form-error">{erro}</div>}

          <div className="resumo-baixa">
            <div>
              <span className="resumo-baixa__rotulo">Ordem de serviço</span>
              <strong>{orcamento.numero}</strong>
            </div>
            <div>
              <span className="resumo-baixa__rotulo">Cliente</span>
              <strong>{orcamento.clienteNome}</strong>
            </div>
            <div>
              <span className="resumo-baixa__rotulo">Placa</span>
              <strong>{orcamento.veiculoDescricao}</strong>
            </div>
            <div>
              <span className="resumo-baixa__rotulo">Total da ordem de serviço</span>
              <strong style={{ color: 'var(--color-primary)' }}>
                {fmtMoeda.format(orcamento.total)}
              </strong>
            </div>
          </div>

          <Field rotulo="Descrição" obrigatorio>
            {(id) => (
              <input
                id={id}
                className="input"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                required
                maxLength={300}
              />
            )}
          </Field>

          <div className="form-row">
            <Field rotulo="Categoria" dica="Opcional. Ex.: Mão de obra, Peças.">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  maxLength={100}
                />
              )}
            </Field>
            <Field rotulo="Vencimento" obrigatorio>
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={vencimento}
                  onChange={(e) => setVencimento(e.target.value)}
                  required
                />
              )}
            </Field>
          </div>

          {itensOrcamento.length > 0 && (
            <>
              <label className="checkbox checkbox--compact">
                <input
                  type="checkbox"
                  checked={usarItens}
                  onChange={(e) => setUsarItens(e.target.checked)}
                />
                <span>
                  Copiar peças e serviços da ordem de serviço ({itensOrcamento.length} item(ns) · total{' '}
                  {fmtMoeda.format(totalItens)})
                </span>
              </label>
              {!usarItens && (
                <Field rotulo="Valor manual" obrigatorio>
                  {(id) => <InputMonetario id={id} valor={valor} onChange={setValor} />}
                </Field>
              )}
            </>
          )}

          {itensOrcamento.length === 0 && (
            <Field rotulo="Valor" obrigatorio>
              {(id) => <InputMonetario id={id} valor={valor} onChange={setValor} />}
            </Field>
          )}

          <Field rotulo="Observações" dica="Opcional.">
            {(id) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                maxLength={2000}
              />
            )}
          </Field>

          <p className="hint">
            Vencimento sugerido: <strong>{fmtData.format(new Date(vencimento))}</strong>. Cliente
            herdado da ordem de serviço.
          </p>
        </form>
      )}
    </Modal>
  );
}
